import type {
  GanttV5Dependency,
  GanttV5Link,
  GanttV5Model,
  GanttV5Task,
  GanttV5Tick,
  GanttV5TimeScale,
} from './types';

export const GANTT_V5_HEADER_HEIGHT = 44;

export interface GanttV5Viewport {
  width: number;
  height: number;
  scrollLeft: number;
  scrollTop: number;
}

export interface GanttV5TaskRect {
  taskId: string;
  rowIndex: number;
  x: number;
  y: number;
  width: number;
  height: number;
  task: GanttV5Task;
  edgeAnchor?: 'left' | 'right' | 'top' | 'bottom' | 'collapsed';
}

export interface GanttV5GroupRect {
  groupId: string;
  rowIndex: number;
  x: number;
  y: number;
  width: number;
  height: number;
  taskIds: string[];
  color?: string;
}

export interface GanttV5CanvasTheme {
  background: string;
  surface: string;
  alternateSurface: string;
  border: string;
  borderStrong: string;
  text: string;
  mutedText: string;
  primary: string;
  primarySoft: string;
  success: string;
  warning: string;
  danger: string;
  fontFamily: string;
}

const readToken = (style: CSSStyleDeclaration, token: string): string => (
  style.getPropertyValue(token).trim()
);

export function readGanttV5CanvasTheme(element: HTMLElement): GanttV5CanvasTheme {
  const style = getComputedStyle(element);
  return {
    background: readToken(style, '--wx-bg'),
    surface: readToken(style, '--wx-surface-1'),
    alternateSurface: readToken(style, '--wx-surface-2'),
    border: readToken(style, '--wx-border'),
    borderStrong: readToken(style, '--wx-border-strong'),
    text: readToken(style, '--wx-fg-1'),
    mutedText: readToken(style, '--wx-fg-3'),
    primary: readToken(style, '--wx-blue-600'),
    primarySoft: readToken(style, '--wx-blue-100'),
    success: readToken(style, '--wx-green-500'),
    warning: readToken(style, '--wx-amber-500'),
    danger: readToken(style, '--wx-red-500'),
    fontFamily: readToken(style, '--wx-font-sans'),
  };
}

export function resolveGanttV5CanvasColor(
  color: string | undefined,
  element: HTMLElement,
  fallback: string,
): string {
  if (!color) return fallback;
  const variableMatch = color.match(/^var\((--[^,\s)]+)/);
  if (!variableMatch) return color;
  return getComputedStyle(element).getPropertyValue(variableMatch[1]).trim() || fallback;
}

export function buildGanttV5TaskRects(
  model: GanttV5Model,
  scale: GanttV5TimeScale,
  viewport: GanttV5Viewport,
  rowHeight: number,
): GanttV5TaskRect[] {
  const firstRow = Math.max(0, Math.floor(viewport.scrollTop / rowHeight) - 1);
  const lastRow = Math.min(
    model.rows.length,
    Math.ceil((viewport.scrollTop + Math.max(0, viewport.height - GANTT_V5_HEADER_HEIGHT)) / rowHeight) + 1,
  );
  const rects: GanttV5TaskRect[] = [];

  for (let rowIndex = firstRow; rowIndex < lastRow; rowIndex += 1) {
    for (const taskId of model.taskIdsByRow.get(rowIndex) ?? []) {
      const task = model.taskById.get(taskId);
      if (!task) continue;
      const x = scale.hourToX(task.start) - viewport.scrollLeft;
      const endX = scale.hourToX(task.end) - viewport.scrollLeft;
      const width = Math.max(2, endX - x);
      if (x > viewport.width || x + width < 0) continue;
      rects.push({
        taskId: task.id,
        rowIndex,
        x,
        y: GANTT_V5_HEADER_HEIGHT + rowIndex * rowHeight - viewport.scrollTop + 5,
        width,
        height: Math.max(12, rowHeight - 10),
        task,
      });
    }
  }
  return rects;
}

const resolveConnectionRow = (model: GanttV5Model, task: GanttV5Task): { rowIndex: number; collapsed: boolean } | null => {
  const directRow = model.taskRowById.get(task.id);
  if (directRow !== undefined) return { rowIndex: directRow, collapsed: false };
  let groupId = task.groupId;
  while (groupId) {
    const groupRow = model.groupRowById.get(groupId);
    if (groupRow !== undefined) return { rowIndex: groupRow, collapsed: true };
    groupId = model.groupById.get(groupId)?.parentId;
  }
  return null;
};

export function buildGanttV5ConnectionAnchorRects(
  model: GanttV5Model,
  taskIds: ReadonlySet<string>,
  visibleRects: GanttV5TaskRect[],
  scale: GanttV5TimeScale,
  viewport: GanttV5Viewport,
  rowHeight: number,
): GanttV5TaskRect[] {
  const visibleIds = new Set(visibleRects.map((rect) => rect.taskId));
  const anchors: GanttV5TaskRect[] = [];
  taskIds.forEach((taskId) => {
    if (visibleIds.has(taskId)) return;
    const task = model.taskById.get(taskId);
    if (!task) return;
    const row = resolveConnectionRow(model, task);
    if (!row) return;
    const rawX = scale.hourToX(task.start) - viewport.scrollLeft;
    const rawEndX = scale.hourToX(task.end) - viewport.scrollLeft;
    const rawY = GANTT_V5_HEADER_HEIGHT + row.rowIndex * rowHeight - viewport.scrollTop + rowHeight / 2;
    let edgeAnchor: GanttV5TaskRect['edgeAnchor'] = row.collapsed ? 'collapsed' : undefined;
    let x = rawX;
    let y = rawY;
    let width = Math.max(2, rawEndX - rawX);
    if (rawEndX < 0) {
      x = 2;
      width = 3;
      edgeAnchor = 'left';
    } else if (rawX > viewport.width) {
      x = Math.max(2, viewport.width - 5);
      width = 3;
      edgeAnchor = 'right';
    } else {
      x = Math.max(2, Math.min(viewport.width - 5, rawX));
      width = 3;
    }
    if (rawY < GANTT_V5_HEADER_HEIGHT) {
      y = GANTT_V5_HEADER_HEIGHT + 3;
      edgeAnchor = 'top';
    } else if (rawY > viewport.height) {
      y = Math.max(GANTT_V5_HEADER_HEIGHT + 3, viewport.height - 5);
      edgeAnchor = 'bottom';
    }
    anchors.push({
      taskId,
      rowIndex: row.rowIndex,
      x,
      y: y - 2,
      width,
      height: 4,
      task,
      edgeAnchor: edgeAnchor ?? 'collapsed',
    });
  });
  return anchors;
}

/** Builds edge anchors for relationship endpoints that are outside the loaded model.
 * Domain adapters may provide endpoint spans on dependencies; share links at least
 * preserve an explicit external-member marker when only one member is loaded. */
export function buildGanttV5ExternalConnectionAnchorRects(
  model: GanttV5Model,
  dependencies: GanttV5Dependency[],
  links: GanttV5Link[],
  knownRects: GanttV5TaskRect[],
  scale: GanttV5TimeScale,
  viewport: GanttV5Viewport,
): GanttV5TaskRect[] {
  const rectById = new Map(knownRects.map((rect) => [rect.taskId, rect]));
  const anchors = new Map<string, GanttV5TaskRect>();
  const add = (
    taskId: string,
    counterpart: GanttV5TaskRect | undefined,
    start: number | undefined,
    end: number | undefined,
    fallbackEdge: 'left' | 'right',
  ) => {
    if (!counterpart || model.taskById.has(taskId) || rectById.has(taskId) || anchors.has(taskId)) return;
    const hasSpan = Number.isFinite(start) && Number.isFinite(end);
    const rawStartX = hasSpan ? scale.hourToX(start as number) - viewport.scrollLeft : Number.NaN;
    const rawEndX = hasSpan ? scale.hourToX(end as number) - viewport.scrollLeft : Number.NaN;
    const edge = hasSpan && rawEndX < 0 ? 'left'
      : hasSpan && rawStartX > viewport.width ? 'right'
        : fallbackEdge;
    const x = edge === 'left' ? 2 : Math.max(2, viewport.width - 5);
    const task: GanttV5Task = {
      id: taskId,
      label: '范围外关联操作',
      start: hasSpan ? start as number : scale.start,
      end: hasSpan ? end as number : scale.end,
      readOnly: true,
    };
    anchors.set(taskId, {
      taskId,
      rowIndex: counterpart.rowIndex,
      x,
      y: counterpart.y + counterpart.height / 2 - 2,
      width: 3,
      height: 4,
      task,
      edgeAnchor: edge,
    });
  };

  dependencies.forEach((dependency) => {
    add(
      dependency.from,
      rectById.get(dependency.to) ?? anchors.get(dependency.to),
      dependency.fromStart,
      dependency.fromEnd,
      'left',
    );
    add(
      dependency.to,
      rectById.get(dependency.from) ?? anchors.get(dependency.from),
      dependency.toStart,
      dependency.toEnd,
      'right',
    );
  });
  links.forEach((link) => {
    const counterpart = link.taskIds.map((taskId) => rectById.get(taskId)).find(Boolean);
    link.taskIds.forEach((taskId) => add(taskId, counterpart, undefined, undefined, 'right'));
  });
  return Array.from(anchors.values());
}

export function hitTestGanttV5Task(
  rects: GanttV5TaskRect[],
  x: number,
  y: number,
): { rect: GanttV5TaskRect; edge: 'start' | 'body' | 'end' } | null {
  for (let index = rects.length - 1; index >= 0; index -= 1) {
    const rect = rects[index];
    if (x < rect.x || x > rect.x + rect.width || y < rect.y || y > rect.y + rect.height) continue;
    const edgeWidth = Math.min(7, rect.width / 3);
    if (x <= rect.x + edgeWidth) return { rect, edge: 'start' };
    if (x >= rect.x + rect.width - edgeWidth) return { rect, edge: 'end' };
    return { rect, edge: 'body' };
  }
  return null;
}

export function buildGanttV5GroupRects(
  model: GanttV5Model,
  scale: GanttV5TimeScale,
  viewport: GanttV5Viewport,
  rowHeight: number,
): GanttV5GroupRect[] {
  const firstRow = Math.max(0, Math.floor(viewport.scrollTop / rowHeight) - 1);
  const lastRow = Math.min(
    model.rows.length,
    Math.ceil((viewport.scrollTop + viewport.height - GANTT_V5_HEADER_HEIGHT) / rowHeight) + 1,
  );
  const rects: GanttV5GroupRect[] = [];
  for (let rowIndex = firstRow; rowIndex < lastRow; rowIndex += 1) {
    const row = model.rows[rowIndex];
    if (!row || row.type !== 'group' || !row.groupId || row.showSummaryBar === false) continue;
    const taskIds = model.descendantTaskIdsByGroupId.get(row.groupId) ?? [];
    const groupTasks = taskIds
      .map((taskId) => model.taskById.get(taskId))
      .filter((task): task is GanttV5Task => Boolean(task));
    if (groupTasks.length === 0) continue;
    const start = Math.min(...groupTasks.map((task) => task.start));
    const end = Math.max(...groupTasks.map((task) => task.end));
    const x = scale.hourToX(start) - viewport.scrollLeft;
    const width = Math.max(3, scale.hourToX(end) - scale.hourToX(start));
    if (x > viewport.width || x + width < 0) continue;
    rects.push({
      groupId: row.groupId,
      rowIndex,
      x,
      y: GANTT_V5_HEADER_HEIGHT + rowIndex * rowHeight - viewport.scrollTop + rowHeight / 2 - 4,
      width,
      height: 8,
      taskIds,
      color: row.color,
    });
  }
  return rects;
}

export function hitTestGanttV5Group(
  rects: GanttV5GroupRect[],
  x: number,
  y: number,
): GanttV5GroupRect | null {
  for (let index = rects.length - 1; index >= 0; index -= 1) {
    const rect = rects[index];
    if (x >= rect.x && x <= rect.x + rect.width && y >= rect.y - 4 && y <= rect.y + rect.height + 4) {
      return rect;
    }
  }
  return null;
}

const prepareContext = (
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
): CanvasRenderingContext2D | null => {
  const dpr = window.devicePixelRatio || 1;
  const backingWidth = Math.max(1, Math.floor(width * dpr));
  const backingHeight = Math.max(1, Math.floor(height * dpr));
  if (canvas.width !== backingWidth || canvas.height !== backingHeight) {
    canvas.width = backingWidth;
    canvas.height = backingHeight;
  }
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.setTransform(dpr, 0, 0, dpr, 0, 0);
  context.clearRect(0, 0, width, height);
  return context;
};

export function drawGanttV5StaticLayer(
  canvas: HTMLCanvasElement,
  theme: GanttV5CanvasTheme,
  ticks: GanttV5Tick[],
  viewport: GanttV5Viewport,
  rowHeight: number,
  rowCount: number,
  todayCanvasX?: number,
): void {
  const context = prepareContext(canvas, viewport.width, viewport.height);
  if (!context) return;
  context.fillStyle = theme.background;
  context.fillRect(0, 0, viewport.width, viewport.height);
  context.fillStyle = theme.surface;
  context.fillRect(0, 0, viewport.width, GANTT_V5_HEADER_HEIGHT);

  const firstRow = Math.max(0, Math.floor(viewport.scrollTop / rowHeight));
  const lastRow = Math.min(
    rowCount,
    Math.ceil((viewport.scrollTop + viewport.height - GANTT_V5_HEADER_HEIGHT) / rowHeight) + 1,
  );
  for (let rowIndex = firstRow; rowIndex < lastRow; rowIndex += 1) {
    const y = GANTT_V5_HEADER_HEIGHT + rowIndex * rowHeight - viewport.scrollTop;
    if (rowIndex % 2 === 1) {
      context.fillStyle = theme.alternateSurface;
      context.fillRect(0, y, viewport.width, rowHeight);
    }
    context.strokeStyle = theme.border;
    context.beginPath();
    context.moveTo(0, Math.round(y + rowHeight) + 0.5);
    context.lineTo(viewport.width, Math.round(y + rowHeight) + 0.5);
    context.stroke();
  }

  context.font = `12px ${theme.fontFamily}`;
  context.textBaseline = 'middle';
  for (const tick of ticks) {
    const x = tick.x - viewport.scrollLeft;
    if (x < -1 || x > viewport.width + 1) continue;
    context.strokeStyle = tick.major ? theme.borderStrong : theme.border;
    context.beginPath();
    context.moveTo(Math.round(x) + 0.5, tick.major ? 0 : GANTT_V5_HEADER_HEIGHT / 2);
    context.lineTo(Math.round(x) + 0.5, viewport.height);
    context.stroke();
    if (tick.label) {
      context.fillStyle = tick.major ? theme.text : theme.mutedText;
      context.fillText(tick.label, x + 6, GANTT_V5_HEADER_HEIGHT / 2);
    }
  }
  context.strokeStyle = theme.borderStrong;
  context.beginPath();
  context.moveTo(0, GANTT_V5_HEADER_HEIGHT - 0.5);
  context.lineTo(viewport.width, GANTT_V5_HEADER_HEIGHT - 0.5);
  context.stroke();
  if (todayCanvasX !== undefined) {
    const x = todayCanvasX - viewport.scrollLeft;
    if (x >= 0 && x <= viewport.width) {
      context.strokeStyle = theme.danger;
      context.lineWidth = 1.5;
      context.setLineDash([4, 3]);
      context.beginPath();
      context.moveTo(Math.round(x) + 0.5, 0);
      context.lineTo(Math.round(x) + 0.5, viewport.height);
      context.stroke();
      context.setLineDash([]);
      context.fillStyle = theme.danger;
      context.font = `10px ${theme.fontFamily}`;
      context.fillText('今天', x + 4, 12);
    }
  }
}

const drawRoundedRect = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) => {
  const safeRadius = Math.min(radius, Math.abs(width) / 2, Math.abs(height) / 2);
  context.beginPath();
  context.roundRect(x, y, width, height, safeRadius);
};

const truncateCanvasText = (
  context: CanvasRenderingContext2D,
  label: string,
  maximumWidth: number,
): string => {
  if (maximumWidth <= 0) return '';
  if (context.measureText(label).width <= maximumWidth) return label;
  let candidate = label;
  while (candidate.length > 0 && context.measureText(`${candidate}…`).width > maximumWidth) {
    candidate = candidate.slice(0, -1);
  }
  return candidate ? `${candidate}…` : '';
};

export function drawGanttV5TaskLayer(
  canvas: HTMLCanvasElement,
  root: HTMLElement,
  theme: GanttV5CanvasTheme,
  rects: GanttV5TaskRect[],
  connectionAnchorRects: GanttV5TaskRect[],
  groupRects: GanttV5GroupRect[],
  dependencies: GanttV5Dependency[],
  links: GanttV5Link[],
  viewport: GanttV5Viewport,
): void {
  const context = prepareContext(canvas, viewport.width, viewport.height);
  if (!context) return;
  const rectByTaskId = new Map(
    [...connectionAnchorRects, ...rects].map((rect) => [rect.taskId, rect] as const),
  );

  for (const groupRect of groupRects) {
    context.save();
    context.globalAlpha = 0.34;
    context.fillStyle = resolveGanttV5CanvasColor(groupRect.color, root, theme.primary);
    drawRoundedRect(
      context,
      groupRect.x,
      groupRect.y,
      groupRect.width,
      groupRect.height,
      groupRect.height / 2,
    );
    context.fill();
    context.restore();
  }

  context.lineWidth = 1.5;
  for (const dependency of dependencies) {
    const source = rectByTaskId.get(dependency.from);
    const target = rectByTaskId.get(dependency.to);
    if (!source || !target) continue;
    const sourceUsesStart = dependency.type === 'SS' || dependency.type === 'SF';
    const targetUsesEnd = dependency.type === 'FF' || dependency.type === 'SF';
    const fromX = sourceUsesStart ? source.x : source.x + source.width;
    const toX = targetUsesEnd ? target.x + target.width : target.x;
    const fromY = source.y + source.height / 2;
    const toY = target.y + target.height / 2;
    const middleX = fromX + (toX - fromX) / 2;
    const dependencyColor = dependency.isConflict
      ? theme.danger
      : resolveGanttV5CanvasColor(dependency.color, root, theme.mutedText);
    context.strokeStyle = dependencyColor;
    context.setLineDash(dependency.level && dependency.level > 1 ? [5, 4] : []);
    context.beginPath();
    context.moveTo(fromX, fromY);
    context.lineTo(middleX, fromY);
    context.lineTo(middleX, toY);
    context.lineTo(toX, toY);
    context.stroke();

    const relationState = dependency.isConflict ? '冲突' : dependency.level && dependency.level > 1 ? '软' : '硬';
    const relationLabel = `${dependency.type}${dependency.lag ? ` ${dependency.lag > 0 ? '+' : ''}${dependency.lag}h` : ''} · ${relationState}`;
    context.save();
    context.font = `10px ${theme.fontFamily}`;
    context.textBaseline = 'bottom';
    context.fillStyle = dependencyColor;
    context.fillText(relationLabel, middleX + 3, Math.min(fromY, toY) - 2);
    context.restore();

    const direction = toX >= middleX ? 1 : -1;
    const arrowLength = 6;
    const arrowHalfHeight = 4;
    context.fillStyle = dependencyColor;
    context.beginPath();
    context.moveTo(toX, toY);
    context.lineTo(toX - direction * arrowLength, toY - arrowHalfHeight);
    context.lineTo(toX - direction * arrowLength, toY + arrowHalfHeight);
    context.closePath();
    context.fill();
  }
  context.setLineDash([]);

  for (const link of links) {
    const linkedRects = link.taskIds
      .map((taskId) => rectByTaskId.get(taskId))
      .filter((rect): rect is GanttV5TaskRect => Boolean(rect));
    if (linkedRects.length < 2) continue;
    const anchor = linkedRects[0];
    context.strokeStyle = resolveGanttV5CanvasColor(link.color, root, theme.success);
    context.lineWidth = 1.5;
    context.setLineDash(link.style === 'solid' ? [] : [5, 4]);
    for (const target of linkedRects.slice(1)) {
      const fromX = anchor.x + anchor.width / 2;
      const fromY = anchor.y + anchor.height / 2;
      const toX = target.x + target.width / 2;
      const toY = target.y + target.height / 2;
      context.beginPath();
      context.moveTo(fromX, fromY);
      context.bezierCurveTo(fromX, (fromY + toY) / 2, toX, (fromY + toY) / 2, toX, toY);
      context.stroke();
    }
    const externalCount = linkedRects.filter((rect) => rect.edgeAnchor).length;
    if (externalCount > 0) {
      context.setLineDash([]);
      context.fillStyle = resolveGanttV5CanvasColor(link.color, root, theme.success);
      context.font = `10px ${theme.fontFamily}`;
      context.textBaseline = 'bottom';
      context.fillText(`外部成员 +${externalCount}`, anchor.x + anchor.width + 5, anchor.y - 2);
    }
  }
  context.setLineDash([]);

  context.font = `12px ${theme.fontFamily}`;
  context.textBaseline = 'middle';
  for (const rect of rects) {
    const taskColor = resolveGanttV5CanvasColor(rect.task.color, root, theme.primary);
    context.fillStyle = taskColor;
    drawRoundedRect(context, rect.x, rect.y, rect.width, rect.height, 4);
    context.fill();

    if (rect.task.conflictType) {
      context.strokeStyle = theme.danger;
      context.lineWidth = 2;
      drawRoundedRect(context, rect.x + 1, rect.y + 1, Math.max(1, rect.width - 2), Math.max(1, rect.height - 2), 3);
      context.stroke();
      context.fillStyle = theme.danger;
      context.beginPath();
      context.moveTo(rect.x + rect.width - 10, rect.y);
      context.lineTo(rect.x + rect.width, rect.y);
      context.lineTo(rect.x + rect.width, rect.y + 10);
      context.closePath();
      context.fill();
    }

    if (typeof rect.task.progress === 'number' && rect.task.progress > 0) {
      context.save();
      drawRoundedRect(context, rect.x, rect.y, rect.width, rect.height, 4);
      context.clip();
      context.globalAlpha = 0.32;
      context.fillStyle = theme.background;
      context.fillRect(rect.x, rect.y, rect.width * Math.min(100, rect.task.progress) / 100, rect.height);
      context.restore();
    }

    context.fillStyle = theme.background;
    const label = truncateCanvasText(context, rect.task.label, rect.width - 12);
    if (label) context.fillText(label, rect.x + 6, rect.y + rect.height / 2);
    if ((rect.task.shareGroups?.length ?? 0) > 0) {
      context.fillStyle = theme.success;
      context.beginPath();
      context.arc(rect.x + rect.width - 5, rect.y + 5, 3, 0, Math.PI * 2);
      context.fill();
    }
  }
}

export function drawGanttV5InteractionLayer(
  canvas: HTMLCanvasElement,
  theme: GanttV5CanvasTheme,
  rects: GanttV5TaskRect[],
  selectedTaskIds: ReadonlySet<string>,
  hoveredTaskId: string | null,
  relatedTaskIds: ReadonlySet<string>,
  previewSpans: Map<string, { start: number; end: number }>,
  scale: GanttV5TimeScale,
  viewport: GanttV5Viewport,
): void {
  const context = prepareContext(canvas, viewport.width, viewport.height);
  if (!context) return;
  context.lineWidth = 2;
  for (const rect of rects) {
    if (!selectedTaskIds.has(rect.taskId) && hoveredTaskId !== rect.taskId && !relatedTaskIds.has(rect.taskId)) continue;
    context.strokeStyle = selectedTaskIds.has(rect.taskId)
      ? theme.primary
      : relatedTaskIds.has(rect.taskId) ? theme.success : theme.borderStrong;
    drawRoundedRect(context, rect.x - 2, rect.y - 2, rect.width + 4, rect.height + 4, 5);
    context.stroke();
  }

  context.globalAlpha = 0.58;
  context.fillStyle = theme.primary;
  for (const [taskId, span] of Array.from(previewSpans.entries())) {
    const source = rects.find((rect) => rect.taskId === taskId);
    if (!source) continue;
    const x = scale.hourToX(span.start) - viewport.scrollLeft;
    const width = Math.max(2, scale.hourToX(span.end) - scale.hourToX(span.start));
    drawRoundedRect(context, x, source.y, width, source.height, 4);
    context.fill();
  }
  context.globalAlpha = 1;
}
